import React, { useState, useEffect, useRef, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material/utils';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDz2lJuhSfA3Vo5g3rcsYTl3BqpAwYsbqc';

let googlePromise = null;
function loadGoogleMaps(language) {
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (googlePromise) return googlePromise;
  googlePromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=${language}`;
    script.async = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return googlePromise;
}

export default function PlacesAutocomplete({ value, onChange, label, locale }) {
  const [inputValue, setInputValue] = useState(value?.formatted || '');
  const [options, setOptions] = useState([]);
  const [google, setGoogle] = useState(null);
  const autocompleteService = useRef(null);
  const geocoder = useRef(null);

  const language = locale === 'he-IL' ? 'he' : locale === 'iw-IL' ? 'he' : locale?.split('-')[0] || 'en';

  useEffect(() => {
    loadGoogleMaps(language).then((g) => {
      setGoogle(g);
      autocompleteService.current = new g.maps.places.AutocompleteService();
      geocoder.current = new g.maps.Geocoder();
    });
  }, [language]);

  // Update input when value changes externally
  useEffect(() => {
    if (value?.formatted) setInputValue(value.formatted);
  }, [value?.formatted]);

  const fetchPredictions = useMemo(
    () =>
      debounce((input, callback) => {
        if (!autocompleteService.current) return;
        autocompleteService.current.getPlacePredictions(
          { input, types: ['geocode', 'establishment'] },
          callback,
        );
      }, 300),
    [],
  );

  useEffect(() => {
    if (!inputValue || inputValue.length < 3 || !google) {
      setOptions([]);
      return;
    }
    fetchPredictions(inputValue, (results) => {
      setOptions(results || []);
    });
  }, [inputValue, google, fetchPredictions]);

  const handleSelect = (_, selected) => {
    if (!selected) {
      onChange({ formatted: '', placeId: '', geocode: { lat: 0, lng: 0 } });
      return;
    }
    geocoder.current.geocode({ placeId: selected.place_id }, (results) => {
      if (!results?.[0]) return;
      const result = results[0];
      const countryComponent = result.address_components.find((c) => c.types?.includes('country'));
      onChange({
        formatted: selected.description,
        placeId: selected.place_id,
        geocode: {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
        },
        countryCode: countryComponent?.short_name || '',
      });
    });
  };

  return (
    <Autocomplete
      freeSolo
      filterOptions={(x) => x}
      options={options}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description || ''
      }
      value={null}
      inputValue={inputValue}
      onInputChange={(_, newValue) => setInputValue(newValue)}
      onChange={handleSelect}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" key={key} {...rest} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <LocationOnIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
            <Box>
              <Typography variant="body2">
                {option.structured_formatting?.main_text || option.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.structured_formatting?.secondary_text || ''}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} fullWidth />
      )}
    />
  );
}
